import React from 'react';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import CloseIcon from '@mui/icons-material/Close';
import { UILink } from '../../components/shared/Links/UILink.styles';
import { ComponentProps } from '../../lib/component-props';
import ButtonPrimary from '../../components/shared/Button/PrimaryButton';
import UiHeading from '../../components/shared/Heading';
import ButtonDiscreet from '../shared/Button/ButtonDiscreet';

export const StModalContainerWithModal = styled(Box)(({ theme }) => {
  return {
    padding: 0,
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    borderRadius: '16px',
    background: theme.palette.white.main,
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    '&:before': {
      content: '""',
      display: 'block',
      background: `linear-gradient(268.67deg, ${theme.palette.themeColorSecondary.main} -16.55%, ${theme.palette.themeColor.main} 73.37%)`,
      height: '16px',
      borderRadius: '16px 16px 0px 0px',
      borderTopLeftRadius: '16px',
      borderTopRightRadius: '16px',
    },
    '&:focus': {
      outline: '0 !important',
    },
    [theme.breakpoints.up('lg')]: {
      minWidth: '521px !important',
    },
    [theme.breakpoints.up('sm')]: {
      minWidth: '593px !important',
      maxWidth: '593px !important',
    },
    '@media screen and (max-width:600px)': {
      minWidth: '340px !important',
    },
    [theme.breakpoints.down(325)]: {
      minWidth: '85% !important',
    },
    '.merge-tag': {
      p: {
        color: '#00839A',
      },
    },
  };
});

export const StmodalContainer = styled(Box)(() => ({
  padding: '24px',
  '@media screen and (max-width:480px)': {
    padding: '24px 18px',
  },
}));

export const StHeadingContainer = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  '@media screen and (max-width:600px)': {
    padding: '1px !important',
  },
}));

export const StHeadingText = styled(Typography)(() => ({
  fontWeight: 600,
  fontSize: '20px',
  lineHeight: '20px',
  color: '#000000',
}));

interface IButtonProps {
  name: string;
}

interface CRModalProps {
  open: boolean;
  handleClose: () => void;
  handleContinue?: () => void;
  handleCancel?: () => void;
  handleActionClick?: (e: any) => void;
  title: string;
  children: any;
  confirmButton?: IButtonProps;
  cancelButton?: IButtonProps;
  ctaButtons?: ComponentProps[];
  showCloseIcon?: boolean;
  isSingleColumn?: boolean;
}
const CRModal = ({
  open,
  handleClose,
  handleContinue,
  handleCancel,
  handleActionClick,
  title,
  children,
  confirmButton,
  cancelButton,
  ctaButtons,
  showCloseIcon = true,
  isSingleColumn = true,
}: CRModalProps): React.ReactElement => {
  return (
    <Modal open={open} aria-labelledby={title}>
      <StModalContainerWithModal>
        <StmodalContainer>
          <StHeadingContainer>
            <Grid>
              <UiHeading id="modal-modal-title" size="h6">
                {title}
              </UiHeading>
            </Grid>
            {showCloseIcon && (
              <IconButton
                id={'close-modal-icon'}
                sx={{ top: '-16px' }}
                color="inherit"
                aria-label="close modal button"
                edge="start"
                onClick={handleClose}
                onKeyUp={(e) => {
                  if (e.key === 'Enter') handleClose();
                }}
                onKeyDown={(e) => {
                  if (e.code === 'Space') handleClose();
                }}
                data-testid="cr-myprofile"
              >
                <CloseIcon />
              </IconButton>
            )}
          </StHeadingContainer>

          {isSingleColumn ? (
            <Grid container sx={{ paddingTop: 2 }}>
              {children}
            </Grid>
          ) : (
            <Grid
              container
              spacing={{ xs: 0, sm: 0, md: 10, lg: 10, xl: 10 }}
              sx={{ paddingTop: 2 }}
            >
              <Grid item xs={12} sm={12} md={10} lg={10} xl={10}>
                {children}
              </Grid>
            </Grid>
          )}

          <br></br>
          <Grid container spacing={2} justifyContent="flex-end">
            {cancelButton && (
              <Grid item xs="auto">
                <ButtonDiscreet onClick={handleCancel}>{cancelButton?.name}</ButtonDiscreet>
              </Grid>
            )}

            {confirmButton && (
              <Grid item xs="auto">
                <ButtonPrimary onClick={handleContinue}>{confirmButton?.name}</ButtonPrimary>
              </Grid>
            )}
            {ctaButtons?.map((a: any, i: number) => (
              <Grid item xs="auto" key={i}>
                <UILink
                  key={a?.name}
                  href={'#'}
                  onClick={(e) => handleActionClick?.(e)}
                  variant={'outline'}
                  id={(a?.fields?.link?.value?.text || a?.name) as string}
                  sx={{
                    textAlign: 'end',
                    ...(i === 0 && { marginLeft: '0px !important', textAlign: 'start' }),
                  }}
                >
                  {a?.fields?.link?.value?.text || a?.name}
                </UILink>
              </Grid>
            ))}
          </Grid>
        </StmodalContainer>
      </StModalContainerWithModal>
    </Modal>
  );
};

export default CRModal;
